import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { 
    getDailyDistributions, 
    getSalaryStatementSummary,
    clearSalarySystemData
} from '../../actions/salarySystem';
import { getFirmOptions } from '../../actions/user';
import DailyDistributionsTabExact from './DailyDistributionsTabExact';
import SalaryStatementsTab from './SalaryStatementsTab';
import './SalarySystem.css';

const SalarySystem = () => {
    const dispatch = useDispatch();
    const history = useHistory();
    const location = useLocation();
    const { firm_options, loading: firmOptionsLoading } = useSelector(state => state.user);
    const { 
        dailyDistributions, 
        salaryStatementSummary
    } = useSelector(state => state.salarySystem);

    // Firm options - you can expand this list
    const firmOptions = [
        { id: 1, name: 'Surxondaryo Qurilish Montaj MCHJ' },
        { id: 2, name: 'Surxondaryo Qurilish Montaj  MCHJ M' },
        { id: 3, name: 'Toirjon XK' },
        { id: 4, name: 'TOIRJON XK' }
    ];

    // Get active tab from URL path
    const getActiveTabFromPath = () => {
        const path = location.pathname;
        if (path.includes('/daily-distributions')) return 'daily-distributions';
        if (path.includes('/salary-statements')) return 'salary-statements';
        if (path === '/salary-system') return 'daily-distributions'; // Base route defaults to daily-distributions
        return 'daily-distributions'; // default
    };

    // Get firm ID from URL parameters
    const getFirmIdFromUrl = () => {
        const urlParams = new URLSearchParams(location.search);
        return urlParams.get('firm_id');
    };

    const [activeTab, setActiveTab] = useState(getActiveTabFromPath());
    const [selectedFirm, setSelectedFirm] = useState(getFirmIdFromUrl()); // Don't set default yet
    const [localLoading, setLocalLoading] = useState(false);
    const [isAutoSelectingFirm, setIsAutoSelectingFirm] = useState(false);
    const loadedFirmRef = useRef(null);

    // Get firm name by ID
    const getFirmName = (firmId) => {
        const firm = (firm_options && firm_options.length > 0 ? firm_options : firmOptions).find(f => f.id === firmId);
        return firm ? firm.name : `Firm ID: ${firmId}`;
    };

    // Update active tab when URL changes
    useEffect(() => {
        setActiveTab(getActiveTabFromPath());
    }, [location.pathname]);

    // Update firm ID when URL parameters change
    useEffect(() => {
        const firmIdFromUrl = getFirmIdFromUrl();
        if (firmIdFromUrl && firmIdFromUrl !== selectedFirm) {
            setSelectedFirm(firmIdFromUrl);
            loadedFirmRef.current = null; // Reset loaded firm ref
        }
    }, [location.search]); // Remove selectedFirm dependency to prevent loops

    // Auto-select first available firm and redirect when no firm is specified
    useEffect(() => {
        const firmIdFromUrl = getFirmIdFromUrl();
        const currentPath = location.pathname;
        
        if (!firmIdFromUrl) {
            // No firm specified in URL, auto-select first available firm
            const availableFirms = firm_options && firm_options.length > 0 ? firm_options : firmOptions;
            
            if (availableFirms.length > 0) {
                setIsAutoSelectingFirm(true);
                const firstFirm = availableFirms[0];
                setSelectedFirm(firstFirm.id);
                
                // If we're on the base /salary-system route, redirect to daily-distributions tab
                if (currentPath === '/salary-system') {
                    const newUrl = `/salary-system/daily-distributions?firm_id=${firstFirm.id}`;
                    history.replace(newUrl);
                } else {
                    // Just add firm_id to current path
                    const newUrl = `${currentPath}?firm_id=${firstFirm.id}`;
                    history.replace(newUrl);
                }
                setIsAutoSelectingFirm(false);
            }
        } else {
            // Firm specified in URL, use it
            setSelectedFirm(firmIdFromUrl);
            
            // If we're on the base /salary-system route, redirect to daily-distributions tab
            if (currentPath === '/salary-system') {
                const newUrl = `/salary-system/daily-distributions?firm_id=${firmIdFromUrl}`;
                history.replace(newUrl);
            }
        }
    }, [firm_options, firmOptions, history, location.pathname]); // Depend on firm options availability and path

    // Fetch firm options when component mounts
    useEffect(() => {
        let isMounted = true;
        
        if (isMounted) {
            dispatch(getFirmOptions());
        }
        
        return () => {
            isMounted = false;
        };
    }, [dispatch]);

    const loadInitialData = useCallback(async () => {
        if (loadedFirmRef.current === selectedFirm) return; // Prevent multiple calls for same firm
        
        try {
            setLocalLoading(true);
            const today = new Date();
            const todayString = today.toISOString().split('T')[0];
            
            const dateParams = {
                from_date: todayString,
                to_date: todayString
            };
            
            await Promise.all([
                dispatch(getDailyDistributions({ 
                    firm_id: selectedFirm,
                    ...dateParams
                })),
                dispatch(getSalaryStatementSummary({ 
                    firm_id: selectedFirm, 
                    ...dateParams
                }))
            ]);
            
            loadedFirmRef.current = selectedFirm; // Mark this firm as loaded
        } catch (error) {
            console.error('Failed to load initial data:', error);
        } finally {
            setLocalLoading(false);
        }
    }, [selectedFirm, dispatch]);

    useEffect(() => {
        let isMounted = true;
        
        // Load initial data when component mounts
        if (selectedFirm && isMounted) {
            loadInitialData();
        }
        
        return () => {
            isMounted = false;
        };
    }, [selectedFirm, loadInitialData]);

    const handleFirmChange = (firmId) => {
        setSelectedFirm(firmId);
        loadedFirmRef.current = null; // Reset loaded firm when changing firms
        // Clear existing data when changing firms
        dispatch(clearSalarySystemData());
        
        // Update URL with firm_id parameter
        if (firmId) {
            const currentPath = location.pathname;
            const newUrl = `${currentPath}?firm_id=${firmId}`;
            history.push(newUrl);
        } else {
            // Remove firm_id parameter when no firm is selected
            const currentPath = location.pathname;
            history.push(currentPath);
        }
    };

    const handleTabChange = (tabName) => {
        setActiveTab(tabName);
        
        // Get current date for parameters
        const today = new Date();
        const todayString = today.toISOString().split('T')[0];
        
        // Navigate to the corresponding route with firm_id and date parameters
        let url;
        if (selectedFirm) {
            url = `/salary-system/${tabName}?firm_id=${selectedFirm}&from_date=${todayString}&to_date=${todayString}`;
        } else {
            url = `/salary-system/${tabName}`;
        }
        history.push(url);
    };

        const renderActiveTab = () => {
        if (!selectedFirm) {
            return (
                <div className="firm-selection">
                    <div style={{ textAlign: 'center', color: '#6c757d', padding: '40px' }}>
                        <p>Please select a firm from the dropdown above to view salary system data.</p>
                    </div>
                </div>
            );
        }

        switch (activeTab) {
            case 'daily-distributions':
                return <DailyDistributionsTabExact firmId={selectedFirm} />;
            case 'salary-statements':
                return <SalaryStatementsTab firmId={selectedFirm} />;
            default:
                return <DailyDistributionsTabExact firmId={selectedFirm} />;
        }
    };

    return (
        <div className="salary-system">
            <div className="content-card">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                    <h2>Employee Salary System</h2>
                    <div className="firm-display">
                        <div className="firm-selector">
                            <label htmlFor="firm-select-dropdown">Firm:</label>
                            <select 
                                id="firm-select-dropdown"
                                value={selectedFirm || ''} 
                                onChange={(e) => handleFirmChange(e.target.value ? parseInt(e.target.value) : null)}
                                className="form-select form-select-sm"
                                disabled={firmOptionsLoading}
                                style={{ color: selectedFirm ? 'inherit' : '#6c757d' }}
                            >
                                {firmOptionsLoading ? (
                                    <option>Loading firms...</option>
                                ) : (firm_options && firm_options.length > 0 ? firm_options : firmOptions).length > 0 ? (
                                    <>
                                        <option value="">-- Select a Firm --</option>
                                        {(firm_options && firm_options.length > 0 ? firm_options : firmOptions).map(firm => (
                                            <option key={firm.id} value={firm.id}>
                                                {firm.name}
                                            </option>
                                        ))}
                                    </>
                                ) : (
                                    <option>No firms available</option>
                                )}
                            </select>
                            {firmOptionsLoading && (
                                <div className="firm-loading-spinner">
                                    <div className="spinner"></div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="tab-navigation">
                    <button 
                        className={`tab-button ${activeTab === 'daily-distributions' ? 'active' : ''}`}
                        onClick={() => handleTabChange('daily-distributions')}
                    >
                        Daily Distributions
                    </button>
                    <button 
                        className={`tab-button ${activeTab === 'salary-statements' ? 'active' : ''}`}
                        onClick={() => handleTabChange('salary-statements')}
                    >
                        Salary Statements
                    </button>
                </div>

                <div className="tab-content">
                    {localLoading ? (
                        <div className="loading-spinner">
                            <div className="spinner"></div>
                            <p>Loading...</p>
                        </div>
                    ) : (
                        renderActiveTab()
                    )}
                </div>
            </div>
        </div>
    );
};

export default SalarySystem;
